import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { CreateRepositoryPeriodComment } from 'src/graphql/repository';
import { UserContext } from 'src/context';
import { importAlertState } from 'src/redux/actions/alert';
import FilterTabs from 'src/components/FilterTabs/FilterTabs';
import CommentsTab from 'src/components/comments/CommentsTab';
import './RepositoryCommentsTab.css';
import 'src/components/general/mentionSuggestions.scss';

const RepositoryCommentsTab = ({ data, companyIdOverride, onCommentCreated, submitLoading }) => {
  const { t } = useTranslation('finance');
  const dispatch = useDispatch();
  const userInfo = useContext(UserContext);
  const sessionUserId = useSelector((state) => state.auth?.uid);
  const authorId = userInfo?.id || sessionUserId || "";
  const createRepositoryPeriodComment = CreateRepositoryPeriodComment();

  const [notes, setNotes] = useState(data?.innerComments || []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const effectiveCompanyId = companyIdOverride || data?.companyId;

  React.useEffect(() => {
    setNotes(data?.innerComments || []);
  }, [data]);

  const handleSubmitComment = async ({ cleanedValue }) => {
    if (!authorId) {
      dispatch(
        importAlertState({
          content: t('comments.publishError'),
          state: 'error',
          duration: 5,
        }),
      );
      return;
    }

    const baseInput = {
      user: {
        id: authorId,
        name: userInfo?.name,
        email: userInfo?.email,
      },
      date: new Date(),
      comment: cleanedValue,
    };

    const input = { periodId: data?.id, ...baseInput };

    try {
      setIsSubmitting(true);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 3000),
      );

      const mutationPromise = onCommentCreated
        ? onCommentCreated(input)
        : createRepositoryPeriodComment.executeMutation({
            periodId: data?.id,
            userId: authorId,
            userName: userInfo?.name,
            userEmail: userInfo?.email,
            date: baseInput.date,
            comment: baseInput.comment,
          });

      await Promise.race([mutationPromise, timeoutPromise]);

      setNotes((n) => [...n, baseInput]);
    } catch (error) {
      if (error.message === 'Timeout') {
        dispatch(
          importAlertState({
            content: t('comments.timeoutWarning'),
            state: 'warning',
            duration: 5,
          }),
        );
        setNotes((n) => [...n, { ...baseInput }]);
      } else {
        dispatch(
          importAlertState({
            content: t('comments.publishError'),
            state: 'error',
            duration: 5,
          }),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const commentsCount = notes?.length || 0;
  const tabs = [
    { value: 'comments', label: t('comments.tab'), count: commentsCount > 99 ? '99+' : commentsCount },
  ];

  return (
    <div className="repository-comments-container" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <FilterTabs tabs={tabs} activeTab="comments" onTabChange={() => {}} />
      <div className="repository-comments-wrapper" style={{ flex: 1, overflowY: 'auto', paddingTop: '20px' }}>
        <CommentsTab
          notes={notes}
          companyId={effectiveCompanyId}
          onSubmitComment={handleSubmitComment}
          isSubmitting={isSubmitting || (submitLoading ?? createRepositoryPeriodComment.fetching)}
        />
      </div>
    </div>
  );
};

export default RepositoryCommentsTab;
